<template>
  <div>
    <section-title
      :icon="'fas fa-file-user'"
      container-class="tag-badge-main-info mt-2 mb-2"
      content="Area: Destrezas académicas dirigidas a la instrucción postsecundaria"
    />
    <section-title :has-line="true" content="Capacidades del Estudiante" />

    <div class="questionaire-container">
      <div v-for="(section, index) in questionaire" :key="index">
        <div
          v-if="section.name"
          class="w-100 dark-gray font-weight-bolder mt-4"
          :class="[
            {
              'alert-error-color ':
                validated && section.questions.some(x => !x.isAnswered)
            }
          ]"
        >
          <span>
            <em class="fa fa-info-circle icon-orange"></em>
            {{ section.name }}
          </span>
          <span
            v-if="validated && section.questions.some(x => !x.isAnswered)"
            class="alert-error alert-error-color"
          >
            - Al menos un valor para cada opción
          </span>
        </div>
        <div
          v-for="(question, questionIndex) in section.questions"
          :key="questionIndex"
          class="row question-container"
        >
          <div
            class="question"
            :class="{
              'col vertical': isSingleVertical(question.mode),
              'col-4': !isSingleVertical(question.mode)
            }"
          >
            {{ question.text }}
            <span
              v-if="!question.isAnswered && validated"
              class="alert-error-color"
              >*</span
            >
            <div
              v-if="!question.isAnswered && validated"
              class="alert-error alert-error-color"
            >
              Al menos un valor
            </div>

            <div class="row" v-if="isSingleVertical(question.mode)">
              <custom-radio
                class="w-75"
                v-model="question.answerId"
                :name="'q' + question.id"
                customErrorMsg=""
                :is-inline="false"
                :is-disabled="isReadOnly"
                :options="question.answers"
                @input="checkAnswer(question, $event)"
              >
                <template #option-418-label="{ option } ">
                  <span
                    v-html="replaceGestualLevelSelect(question.id, option)"
                  ></span>
                  <div
                    v-if="!hasSelectAnswerValue(question.id, option)"
                    class="alert-error alert-error-color pl-16w7"
                  >
                    <i class="fas fa-times-circle"></i>
                    Es requerido el nivel
                  </div>
                </template>
              </custom-radio>
            </div>
          </div>
          <div class="col" v-if="isSingleHorizontal(question.mode)">
            <custom-radio
              class="radio-button-container"
              v-model="question.answerId"
              :name="'q' + question.id"
              is-inline
              customErrorMsg=""
              :is-disabled="isReadOnly"
              :auto-col-width="true"
              :options="question.answers"
              @input="checkAnswer(question, $event)"
            >
              <template #option-413-label="{ option } ">
                <span
                  class="label-custom-dropdown"
                  v-html="replaceBrailleLevelSelect(question.id, option)"
                ></span>
                <div
                  v-if="!hasSelectAnswerValue(question.id, option)"
                  class="alert-error alert-error-color pl-5w8 pt-1"
                >
                  <i class="fas fa-times-circle"></i>
                  Es requerido el nivel
                </div>
              </template>
            </custom-radio>
          </div>
          <div class="col" v-if="isNumericText(question.mode)">
            <div class="row">
              <template v-for="(answer, answerIndex) in question.answers">
                <div
                  class="col-2"
                  :class="[{ 'd-flex align-items-center': isReadOnly }]"
                >
                  <span v-if="isReadOnly">{{
                    question.otherAnswerNumber
                  }}</span>
                  <div v-else>
                    <custom-input
                      :id="'q' + answer.id"
                      class="w-100"
                      maxlength="4"
                      type="number"
                      required
                      :key="answerIndex"
                      v-model="question.otherAnswerNumber"
                      @input="numberAnswer(question, answer.id)"
                    />
                    <div
                      v-if="!hasNumericValue(answer.id, question)"
                      class="alert-error alert-error-color pt-1"
                    >
                      <i class="fas fa-times-circle"></i>
                      Es requerido
                    </div>
                  </div>
                </div>
                <label class="col mt-2">{{ answer.label }}</label>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="py-4 d-flex justify-content-end">
      <action-button
        type="button"
        text="Continuar"
        i="fas fa-arrow-alt-circle-right"
        variant="success"
        @click="save()"
      />
    </div>
  </div>
</template>

<script>
import alertMixin from "@quisit/mipe-utils/mixins/alert.mixin.js";
import SectionTitle from "SectionTitle";
import CustomError from "custom-error";
import CustomRadio from "@/components/common/CustomRadio";
import ActionButton from "ActionButton";
import CustomInput from "CustomInput";

const ANSWER_MODE = {
  SINGLE_VERTICAL: 4,
  SINGLE_HORIZONTAL: 5,
  NUMERIC_TEXT: 7
};
const GESTUAL_ANSWER_ID = 418;
const BRAILLE_ANSWER_ID = 413;
const QUESTION_PRIMARY_MEDIA_ID = 844;
const COMPOSED_ANSWERS = [GESTUAL_ANSWER_ID, BRAILLE_ANSWER_ID];

export default {
  name: "StudentCapacity",
  components: {
    CustomError,
    SectionTitle,
    CustomRadio,
    ActionButton,
    CustomInput
  },
  mixins: [alertMixin],
  props: {
    data: {
      type: Object,
      dafault: () => ({})
    },
    value: {
      type: Object,
      dafault: () => ({ answers: [] })
    },
    isReadOnly: {
      type: Boolean,
      dafault: () => false
    }
  },
  data: () => ({
    questionaire: [],
    validated: false
  }),
  computed: {
    isSingleHorizontal() {
      return mode => mode == ANSWER_MODE.SINGLE_HORIZONTAL;
    },
    isSingleVertical() {
      return mode => mode == ANSWER_MODE.SINGLE_VERTICAL;
    },
    isNumericText() {
      return mode => mode == ANSWER_MODE.NUMERIC_TEXT;
    }
  },
  methods: {
    async validate() {
      const valid = this.questionaire
        .flatMap(s => s.questions)
        .every(q => q.isAnswered);

      //Validate special questions after all are responded
      const composedValid = this.questionaire
        .flatMap(({ questions }) => questions.filter(q => q.isAnswered))
        .flatMap(({ id: questionId, answers }) => {
          const selecteds = answers.filter(a => a.isSelected);
          return selecteds.map(a => Object.create({ ...a, questionId }));
        })
        .filter(a => COMPOSED_ANSWERS.includes(a.id))
        .map(a => this.hasSelectAnswerValue(a.questionId, a))
        .every(valid => valid);
      this.validated = true;
      this.$forceUpdate();
      const result = valid && composedValid;
      return {
        valid: result,
        message: result ? "" : "Completar todas las preguntas requeridas"
      };
    },
    getOptions(question) {
      let result = [];
      question.answers.forEach(answer => {
        result.push({
          id: answer.id,
          isSelected: answer.isSelected,
          name: "rbnt-q" + question.id,
          value: answer.id,
          label: answer.text,
          check: answer.isSelected,
          altNumber: answer.otherAnswerNumber,
          altText: answer.otherAnswerText
        });
      });
      return result;
    },
    checkAnswer(question, answerId) {
      question.answers.forEach(answer => {
        answer.isSelected = answerId == answer.id;
      });
      question.isAnswered = true;
      this.updateValue();
      const braileAnswer = question.answers.find(x => x.label.includes("[levelDropdown]"));
      if(braileAnswer && braileAnswer.id != answerId || question.id == QUESTION_PRIMARY_MEDIA_ID && answerId != GESTUAL_ANSWER_ID) {
        if(braileAnswer)this.clearBrailleLevelSelect(question.id, braileAnswer.id);
        else this.clearBrailleLevelSelect(question.id, GESTUAL_ANSWER_ID);
      }
    },
    numberAnswer(question, answerId) {
      question.answers.forEach(answer => {
        answer.isSelected = answerId == answer.id;
      });
      this.updateValue();
    },
    async save() {
      this.$emit("next", this.value);
    },
    updateValue() {
      this.value.answers = this.questionaire.flatMap(({ questions }) => {
        const answered = questions.filter(
          x => x.isAnswered || x.otherAnswerNumber || x.otherAnswerText
        );
        const value = answered.map(question => {
          const answer = question.answers.find(x => x.isSelected);
          return {
            questionId: question.id,
            answerId: answer?.id,
            otherAnswerText: question.otherAnswerText,
            otherAnswerNumber: Number(question.otherAnswerNumber)
          };
        });

        return value;
      });
      this.$emit("input", this.value);
    },
    replaceGestualLevelSelect(questionId, { id, label, altNumber }) {
      const levels = this.data.gestualLevels;
      const container = document.createElement("div");
      const select = document.createElement("select");
      select.id = `levels-select-${id}-${questionId}`;
      select.className = "form-control d-inline";
      select.disabled = this.isReadOnly;
      select.appendChild(new Option("", undefined, !altNumber));
      levels.forEach(x =>
        select.appendChild(new Option(x.name, x.id, x.id === altNumber))
      );
      container.appendChild(select);
      const value = label.replace(
        "[gestualLevelDropdown]",
        container.innerHTML
      );
      return value;
    },
    replaceBrailleLevelSelect(questionId, { id, label, altNumber, name }) {
      const levels = this.data.brailleLevel;
      const container = document.createElement("div");
      const select = document.createElement("select");
      select.id = `levels-select-${id}-${questionId}`;
      select.className =
        "ml-3 center-select form-control d-inline minwith-dropdown";
      select.disabled = this.isReadOnly;
      select.appendChild(new Option("", undefined, !altNumber));
      levels.forEach(x =>
        select.appendChild(new Option(x.name, x.id, x.id === altNumber))
      );
      container.appendChild(select);
      const value = label.replace("[levelDropdown]", container.innerHTML);
      return value;
    },
    hasSelectAnswerValue(questionId, { id, isSelected }) {
      if (!this.validated || !isSelected) return true;
      const key = `#levels-select-${id}-${questionId}`;
      const select = document.querySelector(key);
      const hasValue = Boolean(select.value);
      if (!hasValue) select.classList.add("was-Invalidated");
      else select.classList.remove("was-Invalidated");
      return hasValue;
    },
    hasNumericValue(id, question) {
      if (!this.validated) return true;
      const key = `q${id}`;
      const input = document.querySelector(`#${key} input`);
      if (!input) return true;
      const hasValue = Boolean(question.otherAnswerNumber);
      if (!hasValue) input.classList.add("was-Invalidated");
      else input.classList.remove("was-Invalidated");
      question.isAnswered = hasValue;
      return hasValue;
    },
    clearBrailleLevelSelect(questionId, answerId){
      const key = `#levels-select-${answerId}-${questionId}`;
      const select = document.querySelector(key);
      if(select) select.value = "";
    }
  },
  async mounted() {
    this.questionaire = [];

    this.data.questionaire.forEach(sectionItem => {
      const section = {
        id: sectionItem.id,
        name: sectionItem.name,
        questions: []
      };
      sectionItem.questions.forEach(questionItem => {
        const question = {
          id: questionItem.id,
          text: questionItem.text,
          mode: questionItem.mode,
          answers: this.getOptions(questionItem),
          answerId: questionItem.answers.find(x => x.isSelected)?.id,
          otherAnswerNumber: questionItem.answers.find(x => x.isSelected)
            ?.otherAnswerNumber,
          otherAnswerText: questionItem.answers.find(x => x.isSelected)
            ?.otherAnswerText,
          isAnswered: questionItem.answers.some(x => x.isSelected)
        };
        section.questions.push(question);
      });
      this.questionaire.push(section);
    });
    this.updateValue();
  }
};
</script>
<style scoped lang="scss">
.questionaire-container {
  background-color: #fafafa;
}
.bg-none {
  background: none !important;
}
.icon-orange {
  color: #ff7058;
}
.questionaire-container {
  padding: 20px;
  border-radius: 5px;
}

.question-container {
  padding: 20px 0px;
  border-bottom: solid 1px #dedede;
}

.question {
  font-weight: bold;
}
.question ::v-deep .custom-radio {
  font-weight: normal !important;
}
.question.vertical ::v-deep .custom-radio {
  margin-bottom: 20px;
  margin-top: 20px;
}
.question.vertical ::v-deep .custom-control-label {
  width: 100%;
}
.question.vertical ::v-deep .custom-control-label .form-control {
  width: 150px;
}
.question.vertical ::v-deep .custom-control-label span {
  position: relative;
  top: -7px;
}
.question.vertical ::v-deep .custom-radio-418 {
  margin-bottom: 5px;
}
.question.vertical ::v-deep .custom-radio-419 {
  margin-top: 5px;
}

.label-custom-dropdown {
  display: flex;
}
.label-custom-dropdown ::v-deep .center-select {
  margin-top: -7px;
}

.label-custom-dropdown ::v-deep .minwith-dropdown {
  min-width: 150px;
}
.alert-error-color {
  color: #ff5b5c;
}
.alert-error {
  font-size: 14px;
  text-align: left;
  font: Lato, Regular;
  letter-spacing: 0;
  margin-bottom: 0;
  font-weight: 400;
}
.pl-16w7 {
  padding-left: 14.7rem !important;
}
.pl-5w8 {
  padding-left: 3.8rem !important;
}
</style>
